import React, { useEffect, useMemo, useState } from 'react';
import { RefreshCw, Download, X, ChevronDown, ChevronUp } from 'lucide-react';
import ChartContainer from '../ChartContainer';
import DashboardTabHeader from '../shared/DashboardTabHeader';
import { InfoTip } from '../shared/FormKit';
import StatTile from './StatTile';
import SortableHeader, { usePagedSortableData, TablePager } from '../SortableTable';
import { exportSheetsToExcel } from '../../utils/excelExport';
import { formatDate, formatDateShort } from '../../utils/dateFormat';
import { API_BASE_URL } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { computeCategoryStats, monthLabelOf, UNCATEGORISED } from '../../utils/categoryStats';
import { DEFAULT_CHART_COLORS } from '../../utils/partnerColors';
import { exportExcelBtnCls } from '../shared/ExportExcelButton';

// The By Category tab — where the firm's training resources
// went, by training category (ld_training_categories: one shared list
// covering internal sessions AND External CPD). Analysis only:
// categories never touch the hour arithmetic or the 40-hour target, which is
// why this tab never says "compliance" — that is Mandatory Compliance's word.
//
// Rows, period, loading and refresh come from LDDashboard, exactly like the
// Training Hours tab, so the two always cover the same fetch and period.

/** Hours to one decimal, as a number (floats reach charts as 948.699… otherwise). */
const round1 = (hours) => Number((hours || 0).toFixed(1));

// The three hour buckets keep the same hues their table columns use everywhere
// else on the dashboard (blue / indigo / green text).
const BUCKETS = [
  { key: 'mandatoryHours', label: 'Internal Mandatory', color: '#3B82F6' },
  { key: 'nonMandatoryHours', label: 'Internal Non-mandatory', color: '#6366F1' },
  { key: 'externalHours', label: 'External CPD', color: '#10B981' },
];

/** Category titles are sentences ("Financial and Sustainability Reporting
 *  Standards and Pronouncements") — wrap them into short lines for chart axes
 *  rather than letting chart.js clip them. */
const wrapLabel = (text, width = 24) => {
  const words = String(text).split(' ');
  const lines = [];
  let line = '';
  words.forEach((w) => {
    if (line && (line + ' ' + w).length > width) { lines.push(line); line = w; }
    else line = line ? `${line} ${w}` : w;
  });
  if (line) lines.push(line);
  return lines;
};

const truncate = (text, max = 42) =>
  (String(text).length > max ? `${String(text).slice(0, max - 1)}…` : String(text));

const UNSET = 'Not specified';

const CategoryOverview = ({
  attendance = [],
  cpdRecords = [],
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  loading = false,
  lastUpdated = null,
  onRefresh,
}) => {
  const { hasPermission } = useAuth();
  const [gradeFilter, setGradeFilter] = useState('all');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [expandedCategory, setExpandedCategory] = useState(null);

  // Filter options come from the full row set so a dimension never loses its
  // own options the moment you pick one (same rule as Mandatory Compliance).
  const gradeOptions = useMemo(
    () => [...new Set([...attendance, ...cpdRecords].map((r) => r.grade || UNSET))].sort(),
    [attendance, cpdRecords]
  );
  const departmentOptions = useMemo(
    () => [...new Set([...attendance, ...cpdRecords].map((r) => r.department || UNSET))].sort(),
    [attendance, cpdRecords]
  );

  const inDim = (r) => (
    (gradeFilter === 'all' || (r.grade || UNSET) === gradeFilter)
    && (departmentFilter === 'all' || (r.department || UNSET) === departmentFilter)
  );
  const scopedAttendance = useMemo(
    () => attendance.filter(inDim),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [attendance, gradeFilter, departmentFilter]
  );
  const scopedCpd = useMemo(
    () => cpdRecords.filter(inDim),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cpdRecords, gradeFilter, departmentFilter]
  );

  const stats = useMemo(
    () => computeCategoryStats(scopedAttendance, scopedCpd, startDate, endDate),
    [scopedAttendance, scopedCpd, startDate, endDate]
  );
  const { categories, months, grades, totals } = stats;

  // Coverage denominator: active staff in scope, from the same endpoint the
  // compliance tab reads (it lists every active employee, records or not).
  // Needs both bounds — with the filter cleared to all-time the column shows
  // '—' rather than a percentage over an undefined population.
  const [activeStaff, setActiveStaff] = useState(null);
  useEffect(() => {
    let alive = true;
    if (!startDate || !endDate) { setActiveStaff(null); return undefined; }
    fetch(`${API_BASE_URL}/api/ld/mandatory-compliance?start=${startDate}&end=${endDate}`, { credentials: 'include' })
      .then((res) => (res.ok ? res.json() : null))
      .then((body) => { if (alive) setActiveStaff(body ? (body.data || []) : null); })
      .catch(() => { if (alive) setActiveStaff(null); });
    return () => { alive = false; };
  }, [startDate, endDate]);

  const activeStaffCount = useMemo(() => {
    if (!activeStaff) return null;
    return activeStaff.filter((r) => (
      (gradeFilter === 'all' || (r.grade || UNSET) === gradeFilter)
      && (departmentFilter === 'all' || (r.department || UNSET) === departmentFilter)
    )).length;
  }, [activeStaff, gradeFilter, departmentFilter]);

  const coverageOf = (c) => (
    activeStaffCount ? Math.min(100, (c.participantCount / activeStaffCount) * 100) : null
  );

  const withCoverage = useMemo(
    () => categories.map((c) => ({ ...c, coverage: coverageOf(c) })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [categories, activeStaffCount]
  );

  const {
    rows: categoryPage, sortConfig, requestSort, page, setPage, total,
  } = usePagedSortableData(withCoverage, { key: 'totalHours', direction: 'desc' });

  const topCategory = categories.find((c) => c.category !== UNCATEGORISED) || categories[0] || null;
  const uncategorised = categories.find((c) => c.category === UNCATEGORISED);

  // ── Chart data ─────────────────────────────────────────────────────────────

  const stackedByCategory = {
    labels: categories.map((c) => wrapLabel(c.category)),
    datasets: BUCKETS.map((b) => ({
      label: b.label,
      data: categories.map((c) => round1(c[b.key])),
      backgroundColor: `${b.color}CC`,
      borderColor: b.color,
      borderWidth: 1,
    })),
  };

  const shareDonut = Object.fromEntries(
    categories.filter((c) => c.totalHours > 0).map((c) => [truncate(c.category), round1(c.totalHours)])
  );

  const monthlyTrend = {
    labels: months.map(monthLabelOf),
    datasets: categories.map((c, idx) => ({
      label: truncate(c.category, 32),
      data: months.map((mk) => round1(c.monthly[mk] || 0)),
      backgroundColor: `${DEFAULT_CHART_COLORS[idx % DEFAULT_CHART_COLORS.length]}CC`,
      borderColor: DEFAULT_CHART_COLORS[idx % DEFAULT_CHART_COLORS.length],
      borderWidth: 1,
    })),
  };

  const matrixMax = Math.max(
    0,
    ...categories.flatMap((c) => grades.map((g) => c.gradeHours[g] || 0))
  );

  // Sessions / CPD detail behind one category row, from rows already in memory.
  const categoryDetail = useMemo(() => {
    if (!expandedCategory) return null;
    const match = (title) => (title || UNCATEGORISED) === expandedCategory;
    const sessions = new Map();
    stats.filteredAttendance.filter((r) => match(r.categoryTitle)).forEach((r) => {
      const key = r.sessionCode || `${r.courseTitle}|${r.date}`;
      if (!sessions.has(key)) {
        sessions.set(key, {
          sessionCode: r.sessionCode,
          courseTitle: r.courseTitle,
          date: r.date,
          isMandatory: r.isMandatory,
          attendees: 0,
          hours: 0,
        });
      }
      const sRow = sessions.get(key);
      sRow.attendees += 1;
      sRow.hours += r.trainingHours || 0;
    });
    const cpdTitles = new Map();
    stats.filteredCpdRecords.filter((r) => match(r.categoryTitle)).forEach((r) => {
      const key = r.trainingTitle || '(untitled)';
      if (!cpdTitles.has(key)) {
        cpdTitles.set(key, { trainingTitle: key, records: 0, participants: new Set(), hours: 0 });
      }
      const cRow = cpdTitles.get(key);
      cRow.records += 1;
      cRow.participants.add(r.participantEmail);
      cRow.hours += r.hours || 0;
    });
    return {
      sessions: [...sessions.values()].sort((a, b) => (a.date < b.date ? 1 : -1)),
      cpd: [...cpdTitles.values()]
        .map((c) => ({ ...c, participants: c.participants.size }))
        .sort((a, b) => b.hours - a.hours),
    };
  }, [expandedCategory, stats]);

  const periodLabel = (startDate || endDate)
    ? `${startDate ? formatDate(startDate) : 'Beginning'} – ${endDate ? formatDate(endDate) : 'Present'}`
    : 'All time';

  const handleExport = () => {
    const summary = withCoverage.map((c) => ({
      'Period': periodLabel,
      'Category': c.category,
      'Internal Sessions': c.sessionCount,
      'External CPD Records': c.externalRecords,
      'Internal Mandatory Hours': round1(c.mandatoryHours),
      'Internal Non-mandatory Hours': round1(c.nonMandatoryHours),
      'External CPD Hours': round1(c.externalHours),
      'Total Hours': round1(c.totalHours),
      'Participants': c.participantCount,
      'Coverage of Active Staff (%)': c.coverage != null ? Number(c.coverage.toFixed(1)) : '',
      'Avg Hours per Participant': round1(c.avgHoursPerParticipant),
    }));
    const monthly = months.map((mk) => {
      const row = { 'Month': monthLabelOf(mk) };
      categories.forEach((c) => { row[c.category] = round1(c.monthly[mk] || 0); });
      return row;
    });
    const matrix = categories.map((c) => {
      const row = { 'Category': c.category };
      grades.forEach((g) => { row[g] = round1(c.gradeHours[g] || 0); });
      row['Total'] = round1(c.totalHours);
      return row;
    });
    exportSheetsToExcel([
      { name: 'By Category', rows: summary },
      { name: 'Monthly Trend', rows: monthly },
      { name: 'By Grade', rows: matrix },
    ], 'Training_By_Category.xlsx');
  };

  if (loading && attendance.length === 0) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-2" />
          <p className="text-gray-600">Loading category data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <DashboardTabHeader
        description={
          <>
            Where training hours went, by training category — internal sessions and External CPD side by side. Categories are analytical only.
            <InfoTip className="ml-1.5" text="Categories never touch the hour arithmetic — the mandatory hours target never looks at them. That measurement lives on the Mandatory Compliance tab." />
          </>
        }
        lastUpdated={lastUpdated}
        actions={
          <>
            <button
              onClick={onRefresh}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            {hasPermission('ld:view') && (
              <button
                onClick={handleExport}
                disabled={categories.length === 0}
                className={exportExcelBtnCls}
                title="Category summary, monthly trend and the grade matrix — as filtered"
              >
                <Download className="w-4 h-4" />
                Export Excel
              </button>
            )}
          </>
        }
      />

      {/* Period + dimension filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-center gap-4">
          <label className="text-sm font-medium text-gray-700">Date Range:</label>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">From:</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => onStartDateChange(e.target.value)}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">To:</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => onEndDateChange(e.target.value)}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="flex items-center gap-2">
            <select
              value={gradeFilter}
              onChange={(e) => setGradeFilter(e.target.value)}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white text-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              aria-label="Filter by Grade"
            >
              <option value="all">All Grades</option>
              {gradeOptions.map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
            <select
              value={departmentFilter}
              onChange={(e) => setDepartmentFilter(e.target.value)}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white text-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              aria-label="Filter by Department"
            >
              <option value="all">All Departments</option>
              {departmentOptions.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          {(gradeFilter !== 'all' || departmentFilter !== 'all') && (
            <button
              onClick={() => { setGradeFilter('all'); setDepartmentFilter('all'); }}
              className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-3.5 h-3.5" /> Clear
            </button>
          )}
          <div className="text-sm text-gray-500 ml-auto">
            {stats.filteredAttendance.length} attendance + {stats.filteredCpdRecords.length} External CPD records in scope
          </div>
        </div>
      </div>

      {/* Headline tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatTile label="Total Hours in Period" value={totals.totalHours.toFixed(1)} />
        <StatTile label="Categories with Activity" value={categories.filter((c) => c.totalHours > 0).length} />
        <StatTile
          label="Most Invested Category"
          value={topCategory && topCategory.totalHours > 0
            ? `${totals.totalHours > 0 ? ((topCategory.totalHours / totals.totalHours) * 100).toFixed(0) : 0}%`
            : '—'}
        />
        <StatTile
          label="Uncategorised Hours"
          value={(uncategorised ? uncategorised.totalHours : 0).toFixed(1)}
          tone={uncategorised && uncategorised.totalHours > 0 ? 'amber' : 'slate'}
        />
      </div>
      {topCategory && topCategory.totalHours > 0 && (
        <p className="text-sm text-gray-500 -mt-3">
          Most invested: <span className="font-medium text-gray-700">{topCategory.category}</span>
          {' '}({topCategory.totalHours.toFixed(1)} hrs)
          {uncategorised && uncategorised.totalHours > 0 && (
            <span className="ml-3 text-amber-700">
              Uncategorised rows predate the category fields (0109/0113) — they are filled in as records are next edited.
            </span>
          )}
        </p>
      )}

      {/* Hours by category, stacked by bucket */}
      <ChartContainer
        title="Training Hours by Category"
        subtitle={`${categories.length} categories • ${totals.totalHours.toFixed(1)} hours in total`}
        data={stackedByCategory}
        type="bar"
        multiDataset
        stacked
        height={Math.max(280, categories.length * 64 + 96)}
        options={{
          responsive: true,
          maintainAspectRatio: false,
          indexAxis: 'y',
          plugins: {
            legend: { position: 'top', labels: { usePointStyle: true, padding: 20 } },
            tooltip: {
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              titleColor: 'white',
              bodyColor: 'white',
            },
          },
          scales: {
            x: { stacked: true, beginAtZero: true, grid: { color: 'rgba(0,0,0,0.1)' }, ticks: { color: '#6B7280' } },
            y: { stacked: true, grid: { display: false }, ticks: { color: '#6B7280' } },
          },
        }}
      />

      {/* Share + monthly trend */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartContainer
          title="Share of Total Hours"
          subtitle={`${Object.keys(shareDonut).length} categories • ${totals.totalHours.toFixed(1)} hours`}
          data={shareDonut}
          type="doughnut"
          height={320}
        />
        <ChartContainer
          title="Monthly Trend by Category"
          subtitle={`${months.length} months • internal by session date, external by certificate date`}
          data={monthlyTrend}
          type="bar"
          multiDataset
          stacked
          height={320}
        />
      </div>

      {/* Category × Grade matrix */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-1">Hours by Category × Grade</h3>
        <p className="text-sm text-gray-500 mb-4">
          Who is learning what — total hours (internal + external) per category for each grade. Darker cells carry more hours.
        </p>
        {grades.length === 0 ? (
          <p className="text-sm text-gray-500 py-6 text-center">No records in this period.</p>
        ) : (
          <div className="table-scroll">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                  {grades.map((g) => (
                    <th key={g} className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{g}</th>
                  ))}
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {categories.map((c) => (
                  <tr key={c.category}>
                    <td className="px-4 py-3 text-sm text-gray-900 max-w-xs">{c.category}</td>
                    {grades.map((g) => {
                      const v = c.gradeHours[g] || 0;
                      const alpha = matrixMax > 0 ? Math.min(0.5, (v / matrixMax) * 0.5) : 0;
                      return (
                        <td
                          key={g}
                          className="px-4 py-3 text-sm text-right tabular-nums"
                          style={v > 0 ? { backgroundColor: `rgba(59, 130, 246, ${alpha.toFixed(3)})` } : undefined}
                        >
                          {v > 0 ? v.toFixed(1) : <span className="text-gray-400">—</span>}
                        </td>
                      );
                    })}
                    <td className="px-4 py-3 text-sm text-right font-semibold tabular-nums">{c.totalHours.toFixed(1)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-gray-50 border-t-2 border-gray-200">
                  <td className="px-4 py-3 text-sm font-semibold text-gray-900">All categories</td>
                  {grades.map((g) => (
                    <td key={g} className="px-4 py-3 text-sm text-right font-semibold tabular-nums">
                      {categories.reduce((sum, c) => sum + (c.gradeHours[g] || 0), 0).toFixed(1)}
                    </td>
                  ))}
                  <td className="px-4 py-3 text-sm text-right font-semibold tabular-nums">{totals.totalHours.toFixed(1)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {/* Category table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex flex-wrap items-baseline justify-between gap-2 mb-4">
          <h3 className="text-lg font-semibold text-gray-900 inline-flex items-center gap-1.5">
            Category Summary ({total})
            <InfoTip text="Trainer rows are excluded, exactly as on the Training Hours tab. Coverage = participants ÷ active staff in scope (needs both period bounds). Categories are display-and-filter only — the mandatory hours target never reads them." />
          </h3>
          <p className="text-sm text-gray-500">{periodLabel}</p>
        </div>
        <div className="table-scroll">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <SortableHeader label="Category" sortKey="category" sortConfig={sortConfig} onSort={requestSort} className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" />
                <SortableHeader label="Sessions" sortKey="sessionCount" sortConfig={sortConfig} onSort={requestSort} align="right" className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" />
                <SortableHeader label="CPD Records" sortKey="externalRecords" sortConfig={sortConfig} onSort={requestSort} align="right" className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" />
                <SortableHeader label="Mandatory" sortKey="mandatoryHours" sortConfig={sortConfig} onSort={requestSort} align="right" className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" />
                <SortableHeader label="Non-mandatory" sortKey="nonMandatoryHours" sortConfig={sortConfig} onSort={requestSort} align="right" className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" />
                <SortableHeader label="External" sortKey="externalHours" sortConfig={sortConfig} onSort={requestSort} align="right" className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" />
                <SortableHeader label="Total Hours" sortKey="totalHours" sortConfig={sortConfig} onSort={requestSort} align="right" className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" />
                <SortableHeader label="Participants" sortKey="participantCount" sortConfig={sortConfig} onSort={requestSort} align="right" className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" />
                <SortableHeader label="Coverage" sortKey="coverage" sortConfig={sortConfig} onSort={requestSort} align="right" className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" />
                <SortableHeader label="Avg Hrs" sortKey="avgHoursPerParticipant" sortConfig={sortConfig} onSort={requestSort} align="right" className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" />
                <SortableHeader label="" sortable={false} className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {categoryPage.length === 0 ? (
                <tr>
                  <td colSpan="11" className="px-4 py-8 text-center text-gray-500">No records in this period</td>
                </tr>
              ) : categoryPage.map((c) => {
                const isOpen = expandedCategory === c.category;
                return (
                  <React.Fragment key={c.category}>
                    <tr
                      className={`cursor-pointer transition-colors ${isOpen ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                      onClick={() => setExpandedCategory(isOpen ? null : c.category)}
                      role="button"
                      tabIndex={0}
                      aria-expanded={isOpen}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setExpandedCategory(isOpen ? null : c.category);
                        }
                      }}
                    >
                      <td className={`px-4 py-3 text-sm font-medium text-gray-900 max-w-sm border-l-4 ${isOpen ? 'border-blue-600' : 'border-transparent'}`}>
                        {c.category}
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-gray-500 tabular-nums">{c.sessionCount}</td>
                      <td className="px-4 py-3 text-sm text-right text-gray-500 tabular-nums">{c.externalRecords}</td>
                      <td className="px-4 py-3 text-sm text-right text-blue-600 tabular-nums">{c.mandatoryHours.toFixed(1)}</td>
                      <td className="px-4 py-3 text-sm text-right text-indigo-600 tabular-nums">{c.nonMandatoryHours.toFixed(1)}</td>
                      <td className="px-4 py-3 text-sm text-right text-green-600 tabular-nums">{c.externalHours.toFixed(1)}</td>
                      <td className="px-4 py-3 text-sm text-right font-semibold text-gray-900 tabular-nums">{c.totalHours.toFixed(1)}</td>
                      <td className="px-4 py-3 text-sm text-right text-gray-500 tabular-nums">{c.participantCount}</td>
                      <td className="px-4 py-3 text-sm text-right text-gray-500 tabular-nums">
                        {c.coverage != null ? `${c.coverage.toFixed(0)}%` : '—'}
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-purple-600 font-semibold tabular-nums">{c.avgHoursPerParticipant.toFixed(1)}</td>
                      <td className="px-4 py-3 text-right">
                        <span
                          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border text-xs font-medium whitespace-nowrap transition-colors ${
                            isOpen ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-gray-300 text-gray-600'
                          }`}
                        >
                          {isOpen ? (<>Close <ChevronUp className="w-4 h-4" /></>) : (<>Detail <ChevronDown className="w-4 h-4" /></>)}
                        </span>
                      </td>
                    </tr>
                    {isOpen && categoryDetail && (
                      <tr>
                        <td colSpan="11" className="p-0 bg-blue-50/40 border-l-4 border-blue-600">
                          <div className="px-6 py-4 space-y-4">
                            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                              <div className="rounded-xl border border-blue-200 bg-white p-4">
                                <h4 className="text-sm font-semibold text-gray-900 mb-2">
                                  Internal Sessions ({categoryDetail.sessions.length})
                                </h4>
                                {categoryDetail.sessions.length === 0 ? (
                                  <p className="text-sm text-gray-500 py-2">No internal sessions in this period.</p>
                                ) : (
                                  <div className="max-h-72 overflow-y-auto">
                                    <table className="min-w-full text-sm">
                                      <thead>
                                        <tr className="border-b border-blue-100 text-left text-xs text-gray-500 uppercase">
                                          <th className="py-2 pr-3">Session</th>
                                          <th className="py-2 pr-3">Date</th>
                                          <th className="py-2 pr-3 text-right">Attendees</th>
                                          <th className="py-2 text-right">Hours</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {categoryDetail.sessions.map((sRow) => (
                                          <tr key={sRow.sessionCode || sRow.courseTitle} className="border-b border-blue-50">
                                            <td className="py-2 pr-3">
                                              <div className="text-gray-900 break-words">{sRow.courseTitle}</div>
                                              <div className="text-xs text-gray-600">
                                                {sRow.sessionCode}
                                                {sRow.isMandatory && (
                                                  <span className="ml-2 inline-flex px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-800 text-[10px] font-semibold">Mandatory</span>
                                                )}
                                              </div>
                                            </td>
                                            <td className="py-2 pr-3 whitespace-nowrap text-gray-600">{formatDateShort(sRow.date)}</td>
                                            <td className="py-2 pr-3 text-right text-gray-600 tabular-nums">{sRow.attendees}</td>
                                            <td className="py-2 text-right font-medium text-gray-900 tabular-nums">{sRow.hours.toFixed(1)}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                              </div>
                              <div className="rounded-xl border border-blue-200 bg-white p-4">
                                <h4 className="text-sm font-semibold text-gray-900 mb-2">
                                  External CPD ({categoryDetail.cpd.length} titles)
                                </h4>
                                {categoryDetail.cpd.length === 0 ? (
                                  <p className="text-sm text-gray-500 py-2">No External CPD records in this period.</p>
                                ) : (
                                  <div className="max-h-72 overflow-y-auto">
                                    <table className="min-w-full text-sm">
                                      <thead>
                                        <tr className="border-b border-blue-100 text-left text-xs text-gray-500 uppercase">
                                          <th className="py-2 pr-3">Training</th>
                                          <th className="py-2 pr-3 text-right">Records</th>
                                          <th className="py-2 pr-3 text-right">People</th>
                                          <th className="py-2 text-right">Hours</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {categoryDetail.cpd.map((cRow) => (
                                          <tr key={cRow.trainingTitle} className="border-b border-blue-50">
                                            <td className="py-2 pr-3 text-gray-900 break-words">{cRow.trainingTitle}</td>
                                            <td className="py-2 pr-3 text-right text-gray-600 tabular-nums">{cRow.records}</td>
                                            <td className="py-2 pr-3 text-right text-gray-600 tabular-nums">{cRow.participants}</td>
                                            <td className="py-2 text-right font-medium text-gray-900 tabular-nums">{cRow.hours.toFixed(1)}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        <TablePager page={page} setPage={setPage} total={total} />
        <p className="text-xs text-gray-500 mt-3">
          Hours are attributed by session date (internal) and certificate date (External CPD).
        </p>
      </div>
    </div>
  );
};

export default CategoryOverview;
